import { Fragment } from 'react';
import type { CompareRow } from '../../../content/landings/types';
import { LocaleLink } from '../../../i18n/locale-link';
import { LandingSection } from '../layout/landing-section';

interface Props {
  title: string;
  lead: string;
  headFree: string;
  headPro: string;
  groups: { title: string; rows: CompareRow[] }[];
  ctaLabel: string;
  onCtaClick: () => void;
}

/**
 * «Что бесплатно, а что в Pro» — таблица в обёртке `overflow-x-auto`, чтобы
 * на узком экране скроллилась сама таблица, а не вся страница вбок.
 *
 * Кнопка «Попробовать Pro» в макете — последний элемент ВНУТРИ `#table`
 * (`.wrap > .btn-row`), поэтому живёт здесь, а не отдельным блоком в
 * `start.tsx` — иначе она выпадает из ритма секции и получает свой
 * собственный (нулевой) отступ вместо унаследованного нижнего.
 */

/**
 * Цвет ячейки зависит от ЗНАЧЕНИЯ, а не от колонки (`.yes`/`.no` в макете).
 * В группе «Бесплатно — всем и навсегда» обе колонки показывают зелёную
 * галочку — если красить по колонке, бесплатная колонка гаснет и таблица
 * читается как «бесплатно не даёт ничего», что противоположно её смыслу.
 */
function cellClass(value: string, fallback: string): string {
  if (value === '✓') return 'text-status-active-text font-extrabold';
  if (value === '—') return 'text-[#C6C5BE] font-bold';
  return fallback;
}

export function CompareTable({
  title,
  lead,
  headFree,
  headPro,
  groups,
  ctaLabel,
  onCtaClick,
}: Props) {
  return (
    <LandingSection id="table" top="sm">
      <h2 className="mb-3 text-[clamp(26px,4.2vw,38px)] font-extrabold leading-[1.12] tracking-[-0.02em]">
        {title}
      </h2>
      <p className="max-w-[680px] text-[clamp(16.5px,2.2vw,19px)] text-ink-soft">{lead}</p>

      {/*
        ⚠️ `min-w-[540px]` действует ТОЛЬКО с sm. Безусловный min-width уводил
        колонку Pro за правый край на 390px — а она и есть смысл секции, и
        никакого намёка на её существование не оставалось. Макет решает это
        блоком `@media(max-width:560px)` (landing-pro.html:180–185): снять
        min-width, сжать padding и шрифт, разрешить перенос в ячейках-значениях.
        Здесь то же самое, только записанное mobile-first: узкий экран —
        база, `sm:` — возврат к макету.
      */}
      <div className="mt-[26px] overflow-x-auto rounded-2xl border border-edge bg-bone-surface shadow-m-sm">
        <table className="w-full border-collapse sm:min-w-[540px]">
          <thead>
            <tr>
              <th className="border-b border-edge px-2 py-2.5 sm:px-4 sm:py-3" />
              <th className="border-b border-edge px-2 py-2.5 text-center text-[10.5px] font-bold uppercase tracking-[0.03em] text-ink-soft sm:px-4 sm:py-3 sm:text-xs sm:tracking-[0.05em]">
                {headFree}
              </th>
              <th className="border-b border-edge bg-brand-light px-2 py-2.5 text-center text-[10.5px] font-bold uppercase tracking-[0.03em] text-ink-soft sm:px-4 sm:py-3 sm:text-xs sm:tracking-[0.05em]">
                {headPro}
              </th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <Fragment key={group.title}>
                <tr>
                  <td
                    colSpan={3}
                    className="bg-bone px-2 py-2 text-[11.5px] font-extrabold uppercase tracking-[0.06em] text-ink-muted sm:px-4"
                  >
                    {group.title}
                  </td>
                </tr>
                {group.rows.map((row) => (
                  <tr key={row.label}>
                    <td className="border-b border-edge px-2 py-2.5 text-[13px] text-ink sm:px-4 sm:py-3 sm:text-[15px]">
                      {row.label}
                    </td>
                    <td
                      className={`border-b border-edge px-2 py-2.5 text-center text-[13px] sm:px-4 sm:py-3 sm:text-[15px] ${cellClass(row.free, 'text-ink-soft')}`}
                    >
                      {row.free}
                    </td>
                    <td
                      className={`border-b border-edge bg-brand-light px-2 py-2.5 text-center text-[13px] sm:px-4 sm:py-3 sm:text-[15px] ${cellClass(row.pro, 'font-semibold text-brand')}`}
                    >
                      {row.pro}
                    </td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Отступление 10 (ревью владельца, 2026-08-18): ведёт на /catalog, не
          /plans — только кнопки ВНУТРИ блока «Тарифы» (`PricingThree`) ещё
          продают тариф; эта — как и остальные не-тарифные CTA — на площадку. */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <LocaleLink
          to="/catalog"
          onClick={onCtaClick}
          className="block w-full rounded-lg border-[1.5px] border-brand bg-brand px-6 py-3.5 text-center text-base font-bold text-white no-underline transition-colors hover:border-brand-mid hover:bg-brand-mid sm:inline-block sm:w-auto"
        >
          {ctaLabel}
        </LocaleLink>
      </div>
    </LandingSection>
  );
}
